import { zodResolver } from '@hookform/resolvers/zod';
import {
  CONTACT_TYPES,
  contactSchema,
  EMAIL_LABELS,
  PHONE_LABELS,
  formatDistrict,
  type ContactType,
} from '@erp/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link } from 'react-router';
import type { z } from 'zod';

import { api } from '../../lib/api.ts';
import {
  useAssignees,
  useChannels,
  useDistrictSearch,
  type ContactDetail,
  type DuplicateMatch,
} from '../../lib/contacts-api.ts';
import { applyApiErrors } from '../../lib/form-errors.ts';
import { useSession } from '../../lib/session.ts';
import { Alert } from '../ui/alert.tsx';
import { Button } from '../ui/button.tsx';
import { Card } from '../ui/card.tsx';
import { Combobox } from '../ui/combobox.tsx';
import { Field, Input, Select } from '../ui/field.tsx';

type Values = z.input<typeof contactSchema>;
type Output = z.output<typeof contactSchema>;

/** What happens to a sensitive field that is already stored (its value is never sent to the form). */
type SensitiveMode = 'keep' | 'change' | 'remove';

/** Typed field-array path ("phones.0") without a number in a template literal. */
const at = <T extends string>(prefix: T, index: number) =>
  `${prefix}.${String(index)}` as `${T}.${number}`;

const MATCH_LABELS = { phone: 'teléfono', email: 'correo', nationalId: 'DUI' } as const;

/**
 * Create or edit a contact. Identity document and exact address: on a new contact they
 * are simple fields; on an existing one they are hidden ("registrado") and can be
 * replaced or removed, never shown here.
 */
export function ContactForm({
  contact,
  onSaved,
  submitLabel,
}: {
  contact?: ContactDetail;
  onSaved: (saved: ContactDetail) => void;
  submitLabel: string;
}) {
  const { data: session } = useSession();
  const assignees = useAssignees();
  const channels = useChannels();
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
  const [nationalIdMode, setNationalIdMode] = useState<SensitiveMode>(
    contact?.hasNationalId ? 'keep' : 'change',
  );
  const [addressMode, setAddressMode] = useState<SensitiveMode>(
    contact?.hasAddress ? 'keep' : 'change',
  );
  const [districtQuery, setDistrictQuery] = useState(
    contact?.district
      ? formatDistrict({
          name: contact.district.name,
          municipality: { id: 0, name: contact.district.municipality },
        })
      : '',
  );
  const districts = useDistrictSearch(districtQuery);

  const form = useForm<Values, unknown, Output>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      kind: contact?.kind ?? 'person',
      name: contact?.name ?? '',
      phones: contact?.phones.map((p) => ({
        number: p.number,
        label: p.label,
        whatsapp: p.whatsapp,
      })) ?? [{ number: '', label: 'mobile', whatsapp: true }],
      emails: contact?.emails.map((e) => ({ address: e.address, label: e.label })) ?? [],
      nationalId: '',
      address: '',
      districtId: contact?.district?.id ?? null,
      channelId: contact?.channel?.id ?? '',
      assignedMembershipId: contact?.assignedTo.membershipId ?? session?.membership.id,
      types: contact?.types.filter((t) => t !== 'owner') ?? [],
      privateNotes: contact?.privateNotes ?? '',
    },
  });
  const phones = useFieldArray({ control: form.control, name: 'phones' });
  const emails = useFieldArray({ control: form.control, name: 'emails' });
  const errors = form.formState.errors;

  const checkDuplicates = async () => {
    const values = form.getValues();
    const body = {
      phones: (values.phones ?? []).map((p) => p.number).filter(Boolean),
      emails: (values.emails ?? []).map((e) => e.address).filter(Boolean),
      nationalId: nationalIdMode === 'change' && values.nationalId ? values.nationalId : null,
      ...(contact ? { excludeId: contact.id } : {}),
    };
    if (body.phones.length === 0 && body.emails.length === 0 && !body.nationalId) {
      setDuplicates([]);
      return;
    }
    try {
      setDuplicates(await api<DuplicateMatch[]>('/contacts/duplicates', { method: 'POST', body }));
    } catch {
      // Only a hint: a failed check does not block the form.
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    const body = {
      ...values,
      channelId: values.channelId ?? null,
      // Sensitive fields: undefined keeps what is stored, null removes it.
      nationalId:
        nationalIdMode === 'keep'
          ? undefined
          : nationalIdMode === 'remove'
            ? null
            : values.nationalId,
      address:
        addressMode === 'keep' ? undefined : addressMode === 'remove' ? null : values.address,
      // Automatic roles (owner, from properties) are kept by the API.
      types: values.types,
    };
    try {
      const saved = contact
        ? await api<ContactDetail>(`/contacts/${contact.id}`, { method: 'PATCH', body })
        : await api<ContactDetail>('/contacts', { method: 'POST', body });
      onSaved(saved);
    } catch (err) {
      setError(
        applyApiErrors(err, form.setError, [
          'name',
          'phones',
          'emails',
          'nationalId',
          'address',
          'districtId',
          'channelId',
          'assignedMembershipId',
        ]),
      );
    }
  });

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="space-y-6" noValidate>
      {error && <Alert tone="danger">{error}</Alert>}

      <Card className="space-y-4">
        <h2 className="font-semibold">Datos generales</h2>
        <fieldset className="flex gap-4">
          <legend className="sr-only">Tipo de contacto</legend>
          {(['person', 'company'] as const).map((kind) => (
            <label key={kind} className="flex min-h-11 items-center gap-2 text-sm">
              <input type="radio" value={kind} className="size-5" {...form.register('kind')} />
              {kind === 'person' ? 'Persona' : 'Empresa'}
            </label>
          ))}
        </fieldset>
        <Field label="Nombre completo" error={errors.name?.message}>
          {(a11y) => <Input {...a11y} autoComplete="off" {...form.register('name')} />}
        </Field>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Papeles</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {(Object.entries(CONTACT_TYPES) as [ContactType, string][])
              .filter(([type]) => type !== 'owner')
              .map(([type, label]) => (
                <label key={type} className="flex min-h-11 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    value={type}
                    className="size-5"
                    {...form.register('types')}
                  />
                  {label}
                </label>
              ))}
          </div>
          <p className="text-sm text-muted-foreground">
            «Propietario» se asigna solo al vincular una propiedad.
          </p>
        </fieldset>
      </Card>

      <Card className="space-y-4">
        <h2 className="font-semibold">Teléfonos y correos</h2>
        {(errors.phones?.root?.message ?? errors.phones?.message) && (
          <p className="text-sm text-danger" role="alert">
            {errors.phones.root?.message ?? errors.phones.message}
          </p>
        )}
        {phones.fields.map((field, index) => (
          <div key={field.id} className="grid gap-2 sm:grid-cols-[1fr_9rem_auto_auto] sm:items-end">
            <Field
              label={`Teléfono ${String(index + 1)}`}
              error={errors.phones?.[index]?.number?.message}
            >
              {(a11y) => (
                <Input
                  {...a11y}
                  type="tel"
                  inputMode="tel"
                  placeholder="7123 4567"
                  {...form.register(`${at('phones', index)}.number`, {
                    onBlur: () => void checkDuplicates(),
                  })}
                />
              )}
            </Field>
            <Field label="Tipo">
              {(a11y) => (
                <Select {...a11y} {...form.register(`${at('phones', index)}.label`)}>
                  {Object.entries(PHONE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-5"
                {...form.register(`${at('phones', index)}.whatsapp`)}
              />
              WhatsApp
            </label>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Quitar teléfono ${String(index + 1)}`}
              onClick={() => {
                phones.remove(index);
              }}
            >
              <Trash2 />
            </Button>
          </div>
        ))}
        {emails.fields.map((field, index) => (
          <div key={field.id} className="grid gap-2 sm:grid-cols-[1fr_9rem_auto] sm:items-end">
            <Field
              label={`Correo ${String(index + 1)}`}
              error={errors.emails?.[index]?.address?.message}
            >
              {(a11y) => (
                <Input
                  {...a11y}
                  type="email"
                  inputMode="email"
                  {...form.register(`${at('emails', index)}.address`, {
                    onBlur: () => void checkDuplicates(),
                  })}
                />
              )}
            </Field>
            <Field label="Tipo">
              {(a11y) => (
                <Select {...a11y} {...form.register(`${at('emails', index)}.label`)}>
                  {Object.entries(EMAIL_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Quitar correo ${String(index + 1)}`}
              onClick={() => {
                emails.remove(index);
              }}
            >
              <Trash2 />
            </Button>
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          {phones.fields.length < 5 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                phones.append({ number: '', label: 'mobile', whatsapp: true });
              }}
            >
              <Plus /> Teléfono
            </Button>
          )}
          {emails.fields.length < 5 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                emails.append({ address: '', label: 'personal' });
              }}
            >
              <Plus /> Correo
            </Button>
          )}
        </div>
        {duplicates.length > 0 && (
          <Alert tone="warning" title="Puede que este contacto ya exista">
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {duplicates.map((d, i) => (
                <li key={d.contact?.id ?? `${d.assignedTo}-${String(i)}`}>
                  Mismo {d.matchedBy.map((m) => MATCH_LABELS[m]).join(', ')}:{' '}
                  {d.contact ? (
                    <Link to={`/contactos/${d.contact.id}`} className="font-medium underline">
                      {d.contact.name}
                    </Link>
                  ) : (
                    <>un contacto asignado a {d.assignedTo} (pídele que te lo comparta)</>
                  )}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </Card>

      <Card className="space-y-4">
        <h2 className="font-semibold">Ubicación y origen</h2>
        <Field
          label="Distrito"
          hint="Donde vive o trabaja: Santa Tecla, Antiguo Cuscatlán…"
          error={errors.districtId?.message}
        >
          {(a11y) => (
            <Combobox
              id={a11y.id}
              describedBy={a11y['aria-describedby']}
              query={districtQuery}
              onQueryChange={(value) => {
                setDistrictQuery(value);
                if (!value) form.setValue('districtId', null);
              }}
              loading={districts.isFetching}
              options={(districts.data ?? []).map((d) => ({
                id: String(d.id),
                label: d.name,
                detail: `${d.municipality.name}, ${d.department.name}`,
              }))}
              onSelect={(option) => {
                form.setValue('districtId', Number(option.id));
                setDistrictQuery(`${option.label} · ${option.detail?.split(',')[0] ?? ''}`);
              }}
              placeholder="Escribe el distrito"
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Canal de origen">
            {(a11y) => (
              <Select
                {...a11y}
                {...form.register('channelId', { setValueAs: (v: string) => v || null })}
              >
                <option value="">Sin canal</option>
                {(channels.data ?? [])
                  .filter((c) => c.active || c.id === contact?.channel?.id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          <Field label="Agente asignado" error={errors.assignedMembershipId?.message}>
            {(a11y) => (
              <Select
                {...a11y}
                {...form.register('assignedMembershipId')}
                disabled={(assignees.data?.length ?? 0) < 2}
              >
                {(assignees.data ?? []).map((m) => (
                  <option key={m.membershipId} value={m.membershipId}>
                    {m.name}
                  </option>
                ))}
                {contact &&
                  !assignees.data?.some(
                    (m) => m.membershipId === contact.assignedTo.membershipId,
                  ) && (
                    <option value={contact.assignedTo.membershipId}>
                      {contact.assignedTo.name}
                    </option>
                  )}
              </Select>
            )}
          </Field>
        </div>
      </Card>

      <Card className="space-y-4">
        <div>
          <h2 className="font-semibold">Datos sensibles</h2>
          <p className="text-sm text-muted-foreground">
            Opcionales hasta el cierre. Se guardan cifrados y solo los ve quien tiene permiso.
          </p>
        </div>
        <SensitiveField
          label="DUI"
          stored={Boolean(contact?.hasNationalId)}
          mode={nationalIdMode}
          onMode={setNationalIdMode}
          error={errors.nationalId?.message}
          input={(a11y) => (
            <Input
              {...a11y}
              inputMode="numeric"
              placeholder="01234567-8"
              {...form.register('nationalId', { onBlur: () => void checkDuplicates() })}
            />
          )}
        />
        <SensitiveField
          label="Dirección exacta"
          stored={Boolean(contact?.hasAddress)}
          mode={addressMode}
          onMode={setAddressMode}
          error={errors.address?.message}
          input={(a11y) => <Input {...a11y} autoComplete="off" {...form.register('address')} />}
        />
      </Card>

      <Card className="space-y-4">
        <Field label="Notas privadas" hint="Solo las ven quienes pueden ver este contacto.">
          {(a11y) => (
            <textarea
              {...a11y}
              rows={4}
              className="w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm"
              {...form.register('privateNotes')}
            />
          )}
        </Field>
      </Card>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Link
          to={contact ? `/contactos/${contact.id}` : '/contactos'}
          className="inline-flex min-h-11 items-center justify-center rounded-lg px-4 text-sm font-medium hover:bg-muted"
        >
          Cancelar
        </Link>
        <Button variant="brand" type="submit" loading={form.formState.isSubmitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function SensitiveField({
  label,
  stored,
  mode,
  onMode,
  error,
  input,
}: {
  label: string;
  stored: boolean;
  mode: SensitiveMode;
  onMode: (mode: SensitiveMode) => void;
  error: string | undefined;
  input: (a11y: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean;
  }) => ReactNode;
}) {
  if (stored && mode !== 'change') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm">
          <span className="font-medium">{label}:</span>{' '}
          {mode === 'remove' ? (
            <span className="text-danger">se quitará al guardar</span>
          ) : (
            'registrado'
          )}
        </p>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            onMode('change');
          }}
        >
          Cambiar
        </Button>
        {mode === 'keep' ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              onMode('remove');
            }}
          >
            Quitar
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              onMode('keep');
            }}
          >
            Conservar
          </Button>
        )}
      </div>
    );
  }
  return (
    <Field
      label={label}
      error={error}
      hint={stored ? 'El valor actual se reemplazará al guardar.' : undefined}
    >
      {input}
    </Field>
  );
}
